import React, { FC, memo, useState, useRef, useCallback } from 'react';

import TailwindEquipmentTypeEditor from '../../../../widgets/bussiness/equipment-type-editor-tw';
import TailwindOemEditor from '../../../../widgets/bussiness/oem-editor-tw';
import TailwindMachineModelEditor from '../../../../widgets/bussiness/machine-model-editor-tw';
import TailwindClusterEditor from '../../../../widgets/bussiness/mrcluster-editor-tw';
import { PatternRowDto } from 'stores/configuration/machinery/pattern-rows-store';
import TailwindMultiMachineUnitEditor from 'widgets/bussiness/multi-machine-unit-editor-tw';
import TailwindMultiPlcTypeEditor from 'widgets/bussiness/multi-plc-type-editor-tw';
import { MachineListItemViewModel } from './machine-list-item';
import { useTranslation } from 'react-i18next';

interface TailwindMachineListItemProps {
  onEditMode?: boolean;
  canDelete?: boolean;
  machine: MachineListItemViewModel;
  pattern?: PatternRowDto;
  automaticallyAddedMachines?: boolean;

  onChange?: (machine: MachineListItemViewModel) => void;
  onDeleteClose?: () => void;
}

const TailwindMachineListItem: FC<TailwindMachineListItemProps> = ({
  onEditMode,
  canDelete,
  pattern,
  machine,
  automaticallyAddedMachines,
  onChange,
  onDeleteClose
}) => {
  const { t } = useTranslation();

  const { cluster, equipmentType, oem, machineModel, machineUnits, plcTypes, machineUnitItems, plcTypeItems } = machine;
  const [relatedClusterIds, setRelatedClusterIds] = useState([]);
  const [relatedEqIds, setRelatedEqIds] = useState([]);

  const lastMachineRef = useRef<string>('');

  const handleOnChange = useCallback(
    (property: keyof MachineListItemViewModel, value: any) => {
      const newMachine = { ...machine };

      newMachine[property] = value;
      if (property === 'cluster' && value?.id !== cluster?.id) {
        newMachine.equipmentType = null;
        newMachine.oem = oem ? (value == null ? null : oem) : null;
        newMachine.machineModel = null;
        newMachine.machineUnits = null;
        newMachine.plcTypes = null;
      }
      if (property === 'equipmentType' && value?.id !== equipmentType?.id) {
        setRelatedClusterIds([]);
        setRelatedEqIds([]);
        // Only update cluster if the new equipmentType has a different one
        if (value?.cluster && (!cluster || value.cluster.id !== cluster.id)) {
          newMachine.cluster = value.cluster;
        }
        newMachine.oem = oem ? (value == null ? null : oem) : null;
        newMachine.machineModel = null;
        newMachine.machineUnits = null;
        newMachine.plcTypes = null;
      }
      if (property === 'oem' && value?.id !== oem?.id) {
        if (!automaticallyAddedMachines) {
          setRelatedClusterIds(value?.relatedClusterIds || []);
          setRelatedEqIds(value?.relatedEqIds || []);
          if (value == null || newMachine.cluster == null || newMachine.equipmentType == null) {
            if (value?.cluster && (!cluster || value.cluster.id !== cluster.id)) {
              newMachine.cluster = value.cluster;
            }
            if (value?.equipmentType && (!equipmentType || value.equipmentType.id !== equipmentType.id)) {
              newMachine.equipmentType = value.equipmentType;
            }
          }
        }
        newMachine.machineUnits = null;
        newMachine.machineModel = null;
        newMachine.plcTypes = null;
      }
      if (property === 'machineModel' && value?.id !== machineModel?.id) {
        setRelatedClusterIds([]);
        setRelatedEqIds([]);
        if (value?.cluster && (!cluster || value.cluster.id !== cluster.id)) {
          newMachine.cluster = value.cluster;
        }
        if (value?.equipmentType && (!equipmentType || value.equipmentType.id !== equipmentType.id)) {
          newMachine.equipmentType = value.equipmentType;
        }
        if (value?.oem && (!oem || value.oem.id !== oem.id)) {
          newMachine.oem = value.oem;
        }
        newMachine.plcTypes = null;
        newMachine.machineUnits = null;
      }

      // Verify it actually changed before calling onChange
      const machineKey = JSON.stringify(newMachine);
      if (lastMachineRef.current !== machineKey) {
        lastMachineRef.current = machineKey;
        onChange(newMachine);
      }
    },
    [machine, cluster, equipmentType, oem, machineModel, automaticallyAddedMachines, onChange]
  );

  const handleOnPlcTypesLoaded = useCallback(
    (items: any[]): void => {
      const hasAvailable = items.length > 0;
      handleOnChange('hasAvailablePlcTypes', hasAvailable);
    },
    [handleOnChange]
  );

  const clusterBlocked = pattern && !!pattern.machineRelatedClusterId;
  const equipmentTypeBlocked = pattern && !!pattern.equipmentTypeId;
  const oemBlocked = pattern && !!pattern.oemId;
  const machineModelBlocked = pattern && !!pattern.machineModelId;

  return (
    <>
      <div className="field required">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Cluster')}</label>
        <TailwindClusterEditor
          nullable
          reloadOnChange
          readOnly={!onEditMode || clusterBlocked || (cluster != null && automaticallyAddedMachines)}
          value={cluster || null}
          onChange={value => handleOnChange('cluster', value)}
          oemId={oem?.id || null}
          relatedClusterIds={relatedClusterIds || []}
          disabled={cluster != null && automaticallyAddedMachines}
        />
      </div>

      <div className="field required">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Equipment Type')}</label>
        <TailwindEquipmentTypeEditor
          nullable
          clearOnReload
          reloadOnChange
          readOnly={!onEditMode || equipmentTypeBlocked || (equipmentType != null && automaticallyAddedMachines)}
          clusterId={cluster?.id || null}
          value={equipmentType}
          onChange={eqType => handleOnChange('equipmentType', eqType)}
          oemId={oem?.id || null}
          relatedEquipmentTypeIds={relatedEqIds || []}
          disabled={equipmentType != null && automaticallyAddedMachines}
        />
      </div>

      <div className="field required">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('OEM')}</label>
        <TailwindOemEditor
          nullable
          clearOnReload
          reloadOnChange
          readonly={!onEditMode || oemBlocked}
          equipmentId={equipmentType?.id || null}
          value={oem}
          onChange={o => handleOnChange('oem', o)}
          clusterId={cluster?.id || null}
        />
      </div>

      <div className="field required">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Machine Model')}</label>
        <TailwindMachineModelEditor
          nullable
          reloadOnChange
          clearOnReload
          readonly={!onEditMode || machineModelBlocked}
          oemId={oem?.id || null}
          equipmentId={equipmentType?.id || null}
          value={machineModel}
          onChange={mm => handleOnChange('machineModel', mm)}
          clusterId={cluster?.id || null}
        />
      </div>

      <div className="field">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Machine Unit')}</label>
        <TailwindMultiMachineUnitEditor
          placeholder={'Select Machine Unit'}
          className="w-full border border-gray-300 rounded px-2 py-1"
          value={machineUnits}
          oemId={oem?.id || null}
          multiple={true}
          readOnly={!onEditMode}
          equipmentId={equipmentType?.id || null}
          onChange={mu => handleOnChange('machineUnits', mu)}
          machineUnitOptions={machineUnitItems}
        />
      </div>

      <div className="field">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('PLC Type')}</label>
        <TailwindMultiPlcTypeEditor
          key={machineModel?.id || ''}
          placeholder={'Select PLC type'}
          className="w-full border border-gray-300 rounded px-2 py-1"
          value={plcTypes}
          readOnly={!onEditMode}
          oemId={oem?.id || null}
          equipmentId={equipmentType?.id || null}
          onChange={pt => handleOnChange('plcTypes', pt)}
          multiple={true}
          plcTypeOptions={plcTypeItems}
          onPlcTypesLoaded={handleOnPlcTypesLoaded}
        />
      </div>

      {onEditMode && canDelete && (
        <div className="flex justify-end">
          <div onClick={onDeleteClose}>
            <svg
              className="w-6 h-6 text-gray-800 dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"
              />
            </svg>
          </div>
        </div>
      )}
    </>
  );
};

// Improved comparison for memo
const areEqual = (prevProps: TailwindMachineListItemProps, nextProps: TailwindMachineListItemProps) => {
  return (
    prevProps.onEditMode === nextProps.onEditMode &&
    prevProps.canDelete === nextProps.canDelete &&
    prevProps.automaticallyAddedMachines === nextProps.automaticallyAddedMachines &&
    prevProps.pattern?.machineRelatedClusterId === nextProps.pattern?.machineRelatedClusterId &&
    prevProps.machine.cluster?.id === nextProps.machine.cluster?.id &&
    prevProps.machine.equipmentType?.id === nextProps.machine.equipmentType?.id &&
    prevProps.machine.oem?.id === nextProps.machine.oem?.id &&
    prevProps.machine.machineModel?.id === nextProps.machine.machineModel?.id &&
    JSON.stringify(prevProps.machine.machineUnits) === JSON.stringify(nextProps.machine.machineUnits) &&
    JSON.stringify(prevProps.machine.plcTypes) === JSON.stringify(nextProps.machine.plcTypes) &&
    JSON.stringify(prevProps.machine.plcTypeItems) === JSON.stringify(nextProps.machine.plcTypeItems) &&
    JSON.stringify(prevProps.machine.machineUnitItems) === JSON.stringify(nextProps.machine.machineUnitItems)
    // DO NOT compare hasAvailablePlcTypes
  );
};

TailwindMachineListItem.defaultProps = { onChange: () => {}, onDeleteClose: () => {} };

export default memo(TailwindMachineListItem, areEqual);
